import React, { Fragment } from 'react';
import { ResultTableProps } from "./ResultTable";
import ResultValue from "../types/ResultValue"
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import PlannjaLogo from '../assets/images/plannja_logo.png';
import PiristeelLogo from '../assets/images/Piristeel_logo.png';
import { useTranslation } from "react-i18next";
import { formatWeight, formatNumber, detectBestLocale } from '../utils/utils';

const styles = StyleSheet.create({
  body: {
    paddingTop: 80,
    paddingBottom: 65,
    paddingHorizontal: 0
  },
  title: {
    fontSize: 24,
    fontFamily: 'Helvetica',
    fontWeight: 'bold',
    textAlign: "center",
    marginBottom: "30px",
  },
  summary: {
    marginHorizontal: "5%",
  },
  
  // 🟢 Tabellens container tar upp 90% av förälderns bredd (för att hantera 5% marginal på varje sida)
  tableContainer: {
    marginHorizontal: "5%",
    marginTop: "5%",
    flexDirection: "column",
    fontFamily: "Helvetica",
    width: "90%", // Tar hänsyn till 5% marginal på varje sida
  },
  
  // 🟢 Header, rader och footer får width 100% så att de fyller hela tableContainer
  headerRow: {
    flexDirection: "row",
    borderBottom: "1px dotted grey",
    fontFamily: "Helvetica-Bold",
    width: "100%",
  },
  row: {
    flexDirection: "row",
    borderTop: "1px dotted grey",
    width: "100%",
  },
  footerRow: {
    flexDirection: "row",
    borderTop: "1px dotted grey",
    width: "100%",
  },

  // 🟢 Kolumnstorlekar - flex används för att balansera bredd
  code: { flex: 1, paddingHorizontal: 3, textAlign: "left" },
  product: { flex: 3, paddingHorizontal: 3, textAlign: "left" }, // Största kolumnen
  amount: { flex: 1, paddingHorizontal: 3, textAlign: "center" },
  weight: { flex: 1, paddingHorizontal: 3, textAlign: "right" },
  pricePerArticle: { flex: 1, paddingHorizontal: 3, textAlign: "right" },
  discount: { flex: 1, paddingHorizontal: 3, textAlign: "right" },
  net_price: { flex: 1, paddingHorizontal: 3, textAlign: "right" },
  
  // 🟢 Footer (colspan-effekt för summeringsraden)
  designationFooter: { flex: 1, paddingHorizontal: 3, textAlign: "left", fontFamily: "Helvetica-Bold" },
  description: { flex: 4, paddingHorizontal: 3, textAlign: "left" }, // Längre kolumn
  totalWeight: { flex: 1, paddingHorizontal: 3, textAlign: "right" },
  netPrice: { flex: 1, paddingHorizontal: 3, textAlign: "right", fontFamily: "Helvetica-Bold" },
  
  page: {
    fontSize: 9,
    flexDirection: "column",
  },
  pageHeader: {
    top: "35px",
    left: "5%",
    position: "absolute"
  },
  pageFooter: {
    position: 'absolute',
    fontSize: 10,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'black',
  },
});

const ResultTablePDF = (props: ResultTableProps) => {
  const { t } = useTranslation("trans");
  const { results, total, discount, productName, fieldSummary, countryCode, fileName } = props;
  const actual_result = results!;
  let logo: string;
  switch (countryCode) {
    case "se": {
      logo = PlannjaLogo;
      break;
    }
    case "no": {
      logo = PlannjaLogo;
      break;
    }
    case "piristeel": {
      logo = PiristeelLogo;
      break;
    }
    default: {
      logo = PlannjaLogo;
      break;
    }
  }

  if (actual_result == null || actual_result.length === 0) return <Document></Document>;
  return (<Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.pageHeader}>
        <Image src={logo} style={{ maxHeight: "50px", maxWidth: "150px", width: "auto", height: "auto" }} />
      </View>
      <View style={styles.body}>
        <Text style={styles.title}>{t("pdf.resultTableTitle")}</Text>
        <Text style={styles.summary}>{productName} {fieldSummary}</Text>
        <ItemsTable results={actual_result} total={total} discount={discount}></ItemsTable>
      </View>
      <Text style={styles.pageFooter} fixed>{fileName}</Text>
    </Page>
  </Document>)
};

const ItemsTable = (props: ResultTableProps) => {
  const { results, total, discount } = props;
  if (!total) return null;
  return (
    <View style={styles.tableContainer}>
      <TableHeader />
      <TableRow style={styles.row} results={results} discount={discount} />
      <TableFooter style={styles.footerRow} totalPrice={total.netPrice} totalWeight={total.weight} />
    </View>

  )
};

const TableHeader = () => {
  const { t } = useTranslation("trans");
  const row = (
    <View style={styles.headerRow}>
      <Text style={styles.code}>{t("table.artNr").toUpperCase()}</Text>
      <Text style={styles.product}>{t("table.term").toUpperCase()}</Text>
      <Text style={styles.amount}>{t("table.qty").toUpperCase()}</Text>
      <Text style={styles.weight}>{t("table.weight").toUpperCase()}</Text>
      <Text style={styles.pricePerArticle}>{t("table.price/art").toUpperCase()}</Text>
      <Text style={styles.discount}>{t("table.discount").toUpperCase()}</Text>
      <Text style={styles.net_price}>{t("table.net_price").toUpperCase()}</Text>
    </View>
  );
  return row;
};

const TableRow = (props: {
  results?: ResultValue[],
  discount?: number,
  style: any
}) => {
  const { results, discount, style } = props;
  const { t } = useTranslation("trans");
  const locale = detectBestLocale();
  
  if (results == null) return null;
  if (style == null) return null;
  const rows = results.map((item, i) => (
    <View style={style} key={i.toString()}>
      <Text style={styles.code}>{item.code}</Text>
      <Text style={styles.product}>{item.name}</Text>
      <Text style={styles.amount}>{item.amount}</Text>
      {/*<Text style={styles.weight}>{item.weight != undefined ? item.weight : "0"}</Text>*/}
      <Text style={styles.weight}>{formatWeight(item.weight, locale, false, 1)}</Text>      
      {/*<Text style={styles.price_per_article}>{ item.price !== undefined ? (Math.round(item.price * 100) / 100).toLocaleString("sv-SE", { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : "0"}</Text>*/}
      <Text style={styles.pricePerArticle}>{formatNumber(item.price, locale, 0)}</Text>
      <Text style={styles.discount}>{discount}</Text>
      {/*<Text style={styles.net_price}>{ item.netPrice !== undefined ? (Math.round(item.netPrice * 100) / 100).toLocaleString("sv-SE", { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : "0"}</Text>*/}
      <Text style={styles.net_price}>{formatNumber(item.netPrice, locale, 0)}</Text>      
    </View>
  ));
  return <Fragment>{rows}</Fragment>;
};

const TableFooter = (props: {
  totalPrice?: number,
  totalWeight?: number,
  style: any
}) => {
  const { totalPrice, totalWeight, style } = props;
  const { t } = useTranslation("trans");
  const locale = detectBestLocale();

  if (style == null) return null;

  const row = (
      <View style={style}>
        <Text style={styles.designationFooter}>{t("table.total")}</Text>
        <Text style={styles.description}>{t("table.description")}</Text>
        <Text style={styles.totalWeight}>{formatWeight(totalWeight, locale, true, 1)}</Text>
        <Text style={styles.pricePerArticle}>&nbsp;</Text>
        <Text style={styles.discount}>&nbsp;</Text>
        {/*<Text style={styles.netPrice}>{ totalPrice !== undefined ? (Math.round(totalPrice * 100) / 100).toLocaleString("sv-SE", { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : "0"}</Text>*/}
        <Text style={styles.netPrice}>{formatNumber(totalPrice, locale, 0)}</Text>
      </View>
  );
  return row;
};

export { ResultTablePDF };
